import type { PowerCmsRecord } from '@/lib/powers-cms';
import { fixMojibakeDeep } from '@/lib/fix-mojibake';
import { migratePowerLoreFields } from '@/lib/power-lore';
import { apiDelete, apiGet, apiPost, apiPut } from './api-client';

/** Payload limpo: nunca envia PowerInfo.status (false|'limited') como publication status. */
function toPowerApiPayload(input: Partial<PowerCmsRecord>): Record<string, unknown> {
  const {
    status: displayStatus,
    publicationStatus,
    createdAt: _c,
    updatedAt: _u,
    ...rest
  } = input;

  const payload: Record<string, unknown> = fixMojibakeDeep({ ...rest });

  if (
    publicationStatus === 'draft' ||
    publicationStatus === 'pending_review' ||
    publicationStatus === 'published'
  ) {
    payload.publicationStatus = publicationStatus;
    payload.status = publicationStatus;
  }

  // data vazia quebra validação `date` no Laravel
  if (payload.released === '' || payload.released === null) {
    delete payload.released;
  }

  // Preservar status de display dentro de content para o backend
  if (displayStatus !== undefined && displayStatus !== 'draft' && displayStatus !== 'pending_review' && displayStatus !== 'published') {
    const content =
      payload.content && typeof payload.content === 'object' && !Array.isArray(payload.content)
        ? { ...(payload.content as Record<string, unknown>) }
        : {};
    content.status = displayStatus;
    payload.content = content;
  }

  return payload;
}

function normalizePower(power: PowerCmsRecord): PowerCmsRecord {
  return migratePowerLoreFields(fixMojibakeDeep(power));
}

export async function getPublicPowers(): Promise<PowerCmsRecord[]> {
  const res = await apiGet<{ data: PowerCmsRecord[] }>('/powers');
  return (res.data ?? []).map(normalizePower);
}

export async function getPublicPower(slug: string): Promise<PowerCmsRecord | null> {
  try {
    const res = await apiGet<{ data: PowerCmsRecord }>(`/powers/${slug}`);
    return res.data ? normalizePower(res.data) : null;
  } catch {
    return null;
  }
}

export async function getAdminPowers(): Promise<PowerCmsRecord[]> {
  const res = await apiGet<{ data: PowerCmsRecord[] }>('/admin/powers', true);
  return (res.data ?? []).map(normalizePower);
}

export async function createPower(input: Partial<PowerCmsRecord>): Promise<PowerCmsRecord> {
  const res = await apiPost<{ data: PowerCmsRecord }>('/admin/powers', toPowerApiPayload(input), true);
  return res.data;
}

export async function updatePower(slug: string, patch: Partial<PowerCmsRecord>): Promise<PowerCmsRecord> {
  const res = await apiPut<{ data: PowerCmsRecord }>(`/admin/powers/${slug}`, toPowerApiPayload(patch), true);
  return res.data;
}

export async function deletePower(slug: string): Promise<void> {
  await apiDelete(`/admin/powers/${slug}`, true);
}

export async function submitPowerForReview(slug: string): Promise<PowerCmsRecord> {
  const res = await apiPost<{ data: PowerCmsRecord }>(`/admin/powers/${slug}/submit-review`, undefined, true);
  return res.data;
}

export async function approvePower(slug: string): Promise<PowerCmsRecord> {
  const res = await apiPost<{ data: PowerCmsRecord }>(`/admin/powers/${slug}/approve`, undefined, true);
  return res.data;
}

export async function rejectPower(slug: string): Promise<PowerCmsRecord> {
  const res = await apiPost<{ data: PowerCmsRecord }>(`/admin/powers/${slug}/reject`, undefined, true);
  return res.data;
}
